import { Box, Heading, Separator } from "@chakra-ui/react-v3";
import { buildDayjs, ISO_DATE } from "@metriport/shared/common/date";
import { SmartCodeableConcept, SmartObservation } from "@metriport/fhir-sdk";
import { ResourceAnalysis } from "../components/resource-analysis";
import { ResourceTimelineLayout } from "../components/resource-timeline-layout";
import { SeriesChart } from "../components/series-chart";
import { SidePanelLayout } from "../components/side-panel-sections/side-panel-layout";
import { VitalRowData } from "../tables/vital";
import { VitalTimelineItem } from "./vital-timeline-item";
import { getDateForResourceType } from "../utils/resource-value-extractors";

export type VitalSidePanelContentProps = {
  data: VitalRowData;
};

export function VitalSidePanelContent({ data }: VitalSidePanelContentProps) {
  const series = data.originalData;
  const shouldShowChart = series.resources.length > 1;
  const isBloodPressure = series.code === "blood-pressure-combined";

  // Sort resources by date (newest first)
  const sortedResources = [...series.resources]
    .map(resource => ({
      resource: resource as SmartObservation,
      date: getDateForResourceType(resource),
    }))
    .filter((item): item is { resource: SmartObservation; date: string } =>
      Boolean(item.date && item.date !== "-")
    )
    .sort((a, b) => buildDayjs(b.date).diff(buildDayjs(a.date)));

  // Extract blood pressure values if applicable
  const getBloodPressureValues = (resource: SmartObservation) => {
    if (!isBloodPressure) return { systolic: undefined, diastolic: undefined };

    const components = resource.component || [];
    // Use SDK's hasLoincCode for checking component codes
    const systolic = components.find(c =>
      (c.code as SmartCodeableConcept | undefined)?.hasLoincCode("8480-6")
    )?.valueQuantity?.value;
    const diastolic = components.find(c =>
      (c.code as SmartCodeableConcept | undefined)?.hasLoincCode("8462-4")
    )?.valueQuantity?.value;

    return { systolic, diastolic };
  };

  return (
    <SidePanelLayout
      title="Vital Sign"
      subtitle={data.observation}
      metadata={`${series.resources.length} readings • ${buildDayjs(series.earliestDate).format(
        ISO_DATE
      )} to ${buildDayjs(series.latestDate).format(ISO_DATE)}`}
    >
      <ResourceAnalysis />

      {shouldShowChart && (
        <>
          <Separator borderColor="border" />
          <Box>
            <Heading size="md" mb={4} color="fg">
              Trend Over Time
            </Heading>
            <Box p={4} borderWidth="1px" borderColor="border" borderRadius="md" bg="bg.subtle">
              <SeriesChart
                series={series}
                chartSeries={data.chartSeries}
                yAxisLabel="Value"
                height={300}
                width={600}
              />
            </Box>
          </Box>
        </>
      )}

      <Separator borderColor="border" />

      <ResourceTimelineLayout
        title={`Readings (${sortedResources.length})`}
        items={sortedResources.map((item, index) => {
          const { systolic, diastolic } = getBloodPressureValues(item.resource);

          return {
            date: item.date,
            content: (
              <VitalTimelineItem
                key={item.resource.id || index}
                resource={item.resource}
                isFirst={index === 0}
                isBloodPressure={isBloodPressure}
                systolicValue={systolic}
                diastolicValue={diastolic}
              />
            ),
          };
        })}
      />
    </SidePanelLayout>
  );
}
